import { Link } from 'react-router-dom';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const howToFind = [
  {
    icon: 'ri-map-pin-line',
    title: 'Search by Location & Tech',
    desc: 'Filter facilities by region, freezing technology (IQF, blast, cold storage), and certification tier to find exactly what your produce needs.',
  },
  {
    icon: 'ri-bar-chart-grouped-line',
    title: 'Compare Capacity & Pricing',
    desc: 'See real-time utilisation rates, available capacity in tonnes, and transparent per-tonne pricing — no hidden charges.',
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'Book Instantly',
    desc: 'Reserve freezing and storage capacity directly through the platform. Confirmation, documentation, and invoicing — all automated.',
  },
];

const benefits = [
  'Access 340+ verified facilities across 28 cities',
  'Find capacity near harvest points — reduce first-mile spoilage',
  'Transparent pricing with no brokerage markups',
  'Full batch traceability from intake to outbound',
  'Flexible terms — book by the tonne, week, or season',
];

export default function SeekerContent() {
  const { ref, revealed } = useScrollReveal();

  return (
    <>
      {/* How to Find */}
      <section className="py-20 md:py-28 bg-background-50">
        <div ref={ref} className={`container-page scroll-reveal ${revealed ? 'revealed' : ''}`}>
          <div className="text-center mb-14">
            <span className="inline-block text-xs font-semibold text-secondary-600 uppercase tracking-widest mb-4">
              For Producers & Traders
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-700 mb-4">
              Find Freezing Capacity Near Your Harvest
            </h2>
            <p className="text-foreground-500 max-w-xl mx-auto">
              Whether you&apos;re a seafood processor, poultry farmer, or vegetable aggregator — there&apos;s certified freezing capacity waiting for your produce.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {howToFind.map((item) => (
              <div key={item.title} className="group bg-card-50 shadow-sm border border-card-200 rounded-xl p-6 md:p-8 text-center hover:-translate-y-1 transition-all duration-300">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600 mb-5 animate-icon-float icon-hover-pop">
                  <i className={`${item.icon} text-xl`}></i>
                </div>
                <h3 className="font-heading font-bold text-primary-700 text-base mb-3">{item.title}</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Benefits checklist */}
          <div className="max-w-xl mx-auto bg-card-50 shadow-sm border border-card-200 rounded-xl p-6 md:p-8">
            <h3 className="font-heading font-bold text-primary-700 text-base mb-4 text-center">
              Why Use Freshhlock Storage
            </h3>
            <div className="space-y-3">
              {benefits.map((benefit) => (
                <div key={benefit} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mt-0.5">
                    <i className="ri-check-line text-xs"></i>
                  </div>
                  <span className="text-sm text-foreground-600">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA for seekers */}
      <section className="py-16 bg-gradient-to-r from-secondary-700 to-brand-700">
        <div className="container-page text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-paper-50 mb-4">
            Ready to find freezing capacity?
          </h2>
          <p className="text-secondary-200/80 mb-8 max-w-md mx-auto">
            Tell us what you need to freeze and where — we&apos;ll match you with verified facilities.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              className="px-8 py-3.5 bg-accent-500 hover:bg-accent-400 text-brand-800 font-semibold text-sm rounded-lg transition-all whitespace-nowrap cursor-pointer"
            >
              Request Capacity
            </Link>
            <Link
              to="/contact"
              className="px-8 py-3.5 border-2 border-paper-50/30 hover:border-paper-50/60 text-paper-50 font-semibold text-sm rounded-lg transition-all whitespace-nowrap cursor-pointer"
            >
              Talk to Storage Team
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}